import { createContext, useContext, useId, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Campo de formulário do Design System (ADR-0065): rótulo de 12px acima,
 * controle hairline abaixo, descrição e erro embaixo. É o `.field` do pacote.
 *
 * O `Field` não clona o filho — ele publica id, `aria-describedby` e estado de
 * erro por contexto, e `Input`/`Textarea`/`Select` consomem. Assim o rótulo
 * fica associado ao controle sem depender de o consumidor lembrar do `htmlFor`,
 * e um controle de terceiro ainda pode ler o contexto quando precisar.
 *
 * Strings ficam com o consumidor (i18n): o primitivo não conhece pt-BR.
 */

interface FieldContexto {
  id: string;
  describedBy?: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContexto | null>(null);

/** Contexto do `Field` mais próximo, ou `null` para controle solto. */
export function useFieldContext(): FieldContexto | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  /** Texto de apoio abaixo do controle. */
  description?: ReactNode;
  /** Mensagem de erro; quando presente o controle vira `aria-invalid`. */
  error?: ReactNode;
  /** Marca visual de obrigatório no rótulo. */
  required?: boolean;
  /** Id do controle; gerado quando omitido. */
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  description,
  error,
  required = false,
  htmlFor,
  className,
  children,
}: FieldProps) {
  const gerado = useId();
  const id = htmlFor ?? gerado;
  const idDescricao = description ? `${id}-desc` : undefined;
  const idErro = error ? `${id}-erro` : undefined;
  const describedBy = [idDescricao, idErro].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cn('flex flex-col', className)}>
        {/*
          O asterisco fica FORA do <label>. Dentro, ele entraria no texto do
          rótulo e todo teste de tela passaria a procurar "Senha *" — o
          significado de obrigatório viaja por `aria-required` no controle.
        */}
        <div className="mb-[5px] flex items-baseline gap-1">
          <label htmlFor={id} className="block text-caption text-fg-dim">
            {label}
          </label>
          {required ? (
            <span aria-hidden className="text-danger">
              *
            </span>
          ) : null}
        </div>
        {children}
        {description ? (
          <p id={idDescricao} className="mt-1 text-caption text-fg-dim">
            {description}
          </p>
        ) : null}
        {error ? (
          <p id={idErro} role="alert" className="mt-1 text-caption text-danger-700">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
